import {html} from "lit";
import {container} from "tsyringe";
import {LockConflictError} from "../../connectivity/lock-conflict.error";
import {UiHelper} from "../../helpers/ui.helper";
import {KnowledgeObject} from "../entities/knowledge-object.entity";
import {formatHolderName} from "./format-holder-name.helper";
import {KnowledgeObjectRepository} from "../repositories/knowledge-object.repository";
import {UserRepository} from "../repositories/user.repository";

/**
 * The "Bearbeiten" click's whole flow: acquire the edit lock, or — when
 * somebody else already holds it — warn, name them, and take it over on
 * confirmation. Shared between `knowledge-rte.component.ts` and
 * `knowledge-whiteboard.component.ts` (Stefan, 2026-09-15: "den gleichen
 * Lock-Mechanismus... wie bei den Linkview-Kacheln") so the two content
 * modes of the same `KnowledgeObject` cannot drift on this policy. Its
 * Linkview sibling is `acquire-linkview-edit-lock.helper.ts` (called from the
 * board toolbar in `o2d-linkview.view.ts`) — same flow, kept separate because
 * it reads the holder from the repository's `items$` row instead of an entity.
 *
 * `entity.lockedBy` IS THE SOURCE OF TRUTH FOR THE HOLDER NAME SHOWN, and it
 * is always CURRENT by the time this reads it: `KnowledgeObjectRepository`
 * refreshes the (identity-cached) entity's lock fields BEFORE a
 * `LockConflictError` rejection settles — same "refresh before the
 * rejection settles" requirement `BackendLinkviewRepository` has for
 * `items$` — so there is no separate re-fetch step here the way Linkview's
 * sidebar re-reads `_items` by uid.
 *
 * EXACTLY ONE RETRY, same bound as Linkview: a second conflict in the same
 * round (somebody raced the take-over between the confirm dialog and the
 * override command reaching the server) means the object is genuinely
 * contended right now, and looping again would advance unattended.
 *
 * @returns `true` once the lock is held by the acting user (acquired
 *          directly, or taken over on confirmation); `false` if the user
 *          declined to take over, or a non-conflict error occurred (already
 *          notified).
 */
export async function acquireKnowledgeObjectEditLock(
    entity: KnowledgeObject,
    isRetry = false,
): Promise<boolean> {
    const repository = container.resolve(KnowledgeObjectRepository);

    try {
        await repository.acquireLock(entity.uid);
        return true;
    } catch (error) {
        if (!(error instanceof LockConflictError)) {
            notifyLockFailed();
            return false;
        }

        if (isRetry) {
            // The retry round is the last one — see the function's own doc
            // comment for why looping further would advance unattended.
            notifyLockFailed();
            return false;
        }

        return confirmAndOverride(entity, repository);
    }
}

/**
 * Resolves the holder's name, warns, and — only on an explicit confirmation
 * — takes the lock over. A holder whose row cannot be resolved is still
 * warned about, named "Unbekannt" (via `formatHolderName`): refusing to warn
 * would make a lock abandoned by a deleted user permanently un-take-over-able.
 */
async function confirmAndOverride(
    entity: KnowledgeObject,
    repository: KnowledgeObjectRepository,
): Promise<boolean> {
    // Review fix (2026-09-16, review-02): explicit guard instead of a bare
    // `entity.lockedBy!` — this is only ever called from the
    // `LockConflictError` branch, where `lockedBy` is practically always
    // set, but nothing at the type level enforced that. Making the
    // invariant explicit turns a hypothetical future misuse (calling this
    // with a free lock) into a defined `false` return instead of a runtime
    // TypeError on `findOneById(null)`.
    if (entity.lockedBy === null) {
        notifyLockFailed();
        return false;
    }
    const holder = await container.resolve(UserRepository).findOneById(entity.lockedBy)
        .catch(() => null);
    const holderName = formatHolderName(holder);

    const confirmed = await UiHelper.confirm({
        title: 'Wird bearbeitet',
        content: html`<p>${holderName} bearbeitet dieses Objekt gerade.
            Wenn Sie fortfahren, wird die Bearbeitung übernommen und
            ${holderName} kann nicht mehr speichern.</p>`,
        confirmLabel: 'Übernehmen',
        cancelLabel: 'Abbrechen',
    });

    if (!confirmed) {
        return false;
    }

    // The expected holder goes out VERBATIM, in the transport spelling
    // `entity.lockedBy` carries it in — see `OverrideEntityLockCommand`'s own
    // doc comment for why converting it breaks every take-over.
    try {
        await repository.overrideLock(entity.uid, entity.lockedBy);
        return true;
    } catch (error) {
        if (error instanceof LockConflictError) {
            // Somebody else raced the take-over. `entity.lockedBy` is already
            // the NEW current holder — or `null`, freed in the meantime —
            // refreshed by the repository before this rejection settled.
            // Re-entering the TOP-LEVEL function (not straight back into a
            // second confirm dialog) is deliberate: if the object is free
            // now, it re-acquires outright with no dialog at all, same as
            // `acquireLinkviewEditLock(refreshed, true)` re-running its
            // whole three-state decision rather than assuming "still
            // contested". `isRetry: true` bounds it to exactly one more round.
            return acquireKnowledgeObjectEditLock(entity, true);
        }
        notifyLockFailed();
        return false;
    }
}

function notifyLockFailed(): void {
    UiHelper.notify({
        level: 'error',
        title: 'Fehler',
        content: html`<p>Die Bearbeitung konnte nicht gestartet werden.</p>`,
        closable: true,
    });
}
